import { ChangeEvent, ReactNode } from 'react';
import {
  ColorPicker as AriaColorPicker,
  ColorPickerProps as AriaColorPickerProps,
  Dialog,
  DialogTrigger,
  ColorSwatch as AriaColorSwatch,
  ColorSwatchProps,
  ColorSwatchPicker as AriaColorSwatchPicker,
  ColorSwatchPickerItem,
  ColorField,
  parseColor,
} from 'react-aria-components';

import { css } from '@emotion/css';

import { Input } from './Input';
import { Popover } from './Popover';

function ColorSwatch(props: ColorSwatchProps) {
  return (
    <AriaColorSwatch
      {...props}
      style={({ color }) => ({
        background: color.toString('hex'),
        width: '32px',
        height: '32px',
        borderRadius: '4px',
        boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.1)',
      })}
    />
  );
}

// colors from https://materialui.co/colors
const colorsets = [
  ['#D32F2F', '#C2185B', '#7B1FA2', '#512DA8', '#303F9F'],
  ['#1976D2', '#0288D1', '#0097A7', '#00796B', '#388E3C'],
  ['#689F38', '#AFB42B', '#FBC02D', '#FFA000', '#F57C00'],
  ['#E64A19', '#5D4037', '#616161', '#455A64', '#690CB0'],
];

function ColorSwatchPicker() {
  return (
    <>
      {colorsets.map((colors, idx) => (
        <AriaColorSwatchPicker
          key={`colorset-${idx}`}
          style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
          }}
        >
          {colors.map(color => (
            <ColorSwatchPickerItem
              key={color}
              color={color}
              className={css({
                position: 'relative',
                outline: 'none',
                borderRadius: '4px',
                width: 'fit-content',
                forcedColorAdjust: 'none',
                cursor: 'pointer',

                '&[data-selected]::after': {
                  // eslint-disable-next-line actual/typography
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  border: '2px solid black',
                  outline: '2px solid white',
                  outlineOffset: '-4px',
                  borderRadius: 'inherit',
                },
              })}
            >
              <ColorSwatch />
            </ColorSwatchPickerItem>
          ))}
        </AriaColorSwatchPicker>
      ))}
    </>
  );
}
const isColor = (value: string) => /^#[0-9a-fA-F]{6}$/.test(value);

interface ColorPickerProps extends AriaColorPickerProps {
  children?: ReactNode;
}

export function ColorPicker({ children, ...props }: ColorPickerProps) {
  const onInput = (value: string) => {
    if (!isColor(value)) {
      return;
    }

    const color = parseColor(value);
    if (color) {
      props.onChange?.(color);
    }
  };

  return (
    <AriaColorPicker {...props}>
      <DialogTrigger>
        {children}
        <Popover>
          <Dialog
            style={{
              outline: 'none',
              padding: '15px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              minWidth: '192px',
              maxHeight: 'inherit',
              boxSizing: 'border-box',
              overflow: 'auto',
            }}
          >
            <ColorSwatchPicker />
            <ColorField
              onInput={({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
                onInput(value)
              }
            >
              <Input placeholder="#RRGGBB" style={{ width: '100px' }} />
            </ColorField>
          </Dialog>
        </Popover>
      </DialogTrigger>
    </AriaColorPicker>
  );
}
